// src/utils/sheetFetcher.ts
// Issue #28：賽程資料唯一入口改走 Cloudflare Worker（KV 極速讀取＋CDN 5 分鐘）。
// 無降級（Owner 決議）：Worker 失敗即 throw，由呼叫端 catch 保留烘入內容（BaseTournamentPage／Index），不白板。
// 舊 GAS→Sheets 直讀路徑已刪除（D1 讀寫全過 GAS 時代結束；CI 快照腳本仍直打 GAS，不在此檔）。
// Worker 公開 GET 端口非 secret；URL 由 build-time 注入（本機 dev 預設 staging Worker）。
import type { GameSchedule } from '../api/types/gameSchedule';

/** staging Worker 公開網址（非 secret，供本機 dev 預設；CI 正式/測試 build 以 secret 覆寫） */
const STAGING_WORKER_URL = 'https://tcfc-score-cache-staging.boday0623.workers.dev';

const WORKER_URL = (import.meta.env.VITE_SCORE_WORKER_URL as string || STAGING_WORKER_URL).replace(/\/+$/, '');

interface WorkerEnvelope {
    updatedAt: string;
    year: string;
    category: string;
    division: string;
    count: number;
    matches: GameSchedule[];
}

/**
 * 從 Worker 讀取指定組別賽程（KV→CDN 層）。
 * @param year - 年度（例 '2025'）
 * @param category - school｜open
 * @param division - 組別鍵（例 'U18'）
 * @returns 該組別賽程（GameSchedule[]，欄位與 Worker envelope 一致）
 */
export const fetchWorkerScores = async (
    year: string | number,
    category: 'school' | 'open',
    division: string | number
): Promise<GameSchedule[]> => {
    // WHY 寬型別：YearKey 是 keyof（含 number，main 既有 TS2345 同源），呼叫端不轉型，在此統一 String()
    const url = `${WORKER_URL}/scores/${encodeURIComponent(String(year))}/${encodeURIComponent(category)}/${encodeURIComponent(String(division))}`;
    let res: Response;
    try {
        res = await fetch(url);
    } catch (networkError) {
        throw new Error(`比分服務連線失敗 (${year}/${category}/${division})`);
    }
    if (res.status === 404) {
        throw new Error(`該組別快取尚未建立 (${year}/${category}/${division})，等下一次同步（30 分內）`);
    }
    if (!res.ok) {
        throw new Error(`比分服務異常 HTTP ${res.status} (${year}/${category}/${division})`);
    }
    const payload = (await res.json()) as Partial<WorkerEnvelope>;
    if (!payload || !Array.isArray(payload.matches)) {
        throw new Error(`比分服務回應異常 (${year}/${category}/${division})`);
    }
    return payload.matches;
};
